import { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LuPackageCheck } from "react-icons/lu";
import { MdOutlineAutorenew } from "react-icons/md";
import { BsHourglassSplit } from "react-icons/bs";
import { IoReceiptOutline } from "react-icons/io5";
import { TbTruckDelivery } from "react-icons/tb";
import { Star, Search, Package } from 'lucide-react';
import { useI18n } from '@/i18n';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/store/authStore';
import { useOrderStore } from '@/store/orderStore';
import type { Order, OrderItem } from '@/utils/supabase';
import { paymentService } from '@/utils/payment';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { OrderCardSkeleton } from '@/components/skeletons/OrderSkeletons';

type OrderTab = 'active' | 'in-review' | 'delivered';

const ACTIVE_STATUSES = ['pending', 'confirmed', 'packaging', 'shipped', 'cancelled'];
const DELIVERED_STATUSES = ['delivered'];

const formatPaymentMethod = (method: string) => {
  if (!method) return '';
  const m = method.toLowerCase();
  if (m === 'bkash') return 'bKash';
  if (m === 'nagad') return 'Nagad';
  if (m === 'cod') return 'COD';
  return method.charAt(0).toUpperCase() + method.slice(1);
};

export function Orders() {
  const { t, currentLang } = useI18n();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { orders, fetchMyOrders, isLoading, totalOrders, realtimeUpdateTick } = useOrderStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<OrderTab>('active');
  const [ratingOrderId, setRatingOrderId] = useState<string | null>(null);
  const [ratingValue, setRatingValue] = useState(0);
  const [ratingStorageVersion, setRatingStorageVersion] = useState(0);
  const [initiatingPaymentId, setInitiatingPaymentId] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchMyOrders(user.id, currentPage, 20);
  }, [user, navigate, fetchMyOrders, currentPage, realtimeUpdateTick]);

  const ratedOrderIds = useMemo(() => {
    if (ratingStorageVersion < 0 || !user?.id) return [];

    try {
      const stored = localStorage.getItem(`chocolate_vibes_order_ratings_${user.id}`);
      return stored ? JSON.parse(stored) as string[] : [];
    } catch {
      return [];
    }
  }, [user?.id, ratingStorageVersion]);

  // Auto-refresh when coming back online
  useEffect(() => {
    const handleOnline = () => {
      if (user) fetchMyOrders(user.id, currentPage, 20);
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [user, fetchMyOrders, currentPage]);

  const { activeOrders, inReviewOrders, deliveredOrders } = useMemo(() => {
    const filtered = orders.filter(order =>
      order.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (order.order_items?.some((item: OrderItem) =>
        (item.products?.name_en || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (item.products?.name_bn || '').toLowerCase().includes(searchQuery.toLowerCase())
      ))
    );
    return {
      activeOrders: filtered.filter((o: Order) => ACTIVE_STATUSES.includes(o.status)),
      inReviewOrders: filtered.filter((o: Order) => DELIVERED_STATUSES.includes(o.status) && !ratedOrderIds.includes(o.id)),
      deliveredOrders: filtered.filter((o: Order) => DELIVERED_STATUSES.includes(o.status) && ratedOrderIds.includes(o.id)),
    };
  }, [orders, searchQuery, ratedOrderIds]);

  const currentOrders = activeTab === 'active' ? activeOrders : activeTab === 'in-review' ? inReviewOrders : deliveredOrders;

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'delivered': return <LuPackageCheck className="h-3.5 w-3.5 text-emerald-700 animate-pulse" />;
      case 'shipped': return <TbTruckDelivery className="h-3.5 w-3.5 text-blue-700 animate-truck-slide" />;
      case 'pending': return <BsHourglassSplit className="h-3.5 w-3.5 text-amber-700 animate-hourglass-flip" />;
      case 'packaging': return <MdOutlineAutorenew className="h-3.5 w-3.5 text-violet-700 animate-spin" style={{ animationDuration: '1.5s' }} />;
      case 'confirmed': return <CheckCircle className="h-3.5 w-3.5 text-gray-700 animate-pulse" />;
      case 'cancelled': return <XCircle className="h-3.5 w-3.5 text-red-700 animate-pulse" />;
      default: return <CheckCircle className="h-3.5 w-3.5 text-gold-700 animate-pulse" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'delivered': return 'bg-emerald-50 text-emerald-700 border-emerald-100';
      case 'shipped': return 'bg-blue-50 text-blue-700 border-blue-100';
      case 'pending': return 'bg-amber-50 text-amber-700 border-amber-100';
      case 'packaging': return 'bg-violet-50 text-violet-700 border-violet-100';
      case 'confirmed': return 'bg-gray-50 text-gray-700 border-gray-100';
      case 'cancelled': return 'bg-red-50 text-red-700 border-red-100';
      default: return 'bg-gold-50 text-gold-700 border-gold-100';
    }
  };



  const getItemCount = (order: Order) => {
    if (!order.order_items) return 0;
    return order.order_items.reduce((sum: number, item: OrderItem) => sum + (item.quantity || 0), 0);
  };

  const getProductImages = (order: Order): string[] => {
    if (!order.order_items) return [];
    return order.order_items
      .map((item: OrderItem) => item.products?.images?.[0])
      .filter((img): img is string => Boolean(img))
      .slice(0, 3);
  };

  const handleRateProduct = (orderId: string) => {
    setRatingOrderId(orderId);
    setRatingValue(0);
  };

  const submitRating = () => {
    if (ratingValue === 0 || !ratingOrderId) return;

    if (user?.id) {
      const next = ratedOrderIds.includes(ratingOrderId) ? ratedOrderIds : [...ratedOrderIds, ratingOrderId];
      localStorage.setItem(`chocolate_vibes_order_ratings_${user.id}`, JSON.stringify(next));
      setRatingStorageVersion((version) => version + 1);
    }

    toast.success(currentLang === 'bn' ? 'ধন্যবাদ! আপনার রেটিং সংরক্ষণ করা হয়েছে।' : 'Thanks! Your rating has been saved.');
    setRatingOrderId(null);
    setRatingValue(0);
  };

  const handlePayNow = async (e: React.MouseEvent, order: Order) => {
    e.stopPropagation();
    if (initiatingPaymentId) return;
    
    setInitiatingPaymentId(order.id);
    toast.info(currentLang === 'bn' ? 'পেমেন্ট গেটওয়েতে রিডাইরেক্ট করা হচ্ছে...' : 'Redirecting to payment gateway...', { duration: 4000 });
    
    try {
      const callbackUrl = `${window.location.origin}/payment/callback?gateway=${order.payment_method}&order_id=${order.id}`;
      const initResult = await paymentService.initiatePayment(
        order.payment_method as 'bkash' | 'nagad', 
        order.total, 
        order.id, 
        callbackUrl
      );
      
      if (initResult.success && initResult.gatewayUrl) {
        window.location.href = initResult.gatewayUrl;
      } else {
        toast.error(initResult.error || 'Payment initiation failed.');
        setInitiatingPaymentId(null);
      }
    } catch(err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Payment initiation error');
      setInitiatingPaymentId(null);
    }
  };

  return (
    <div className="min-h-[100dvh] bg-white font-sans">
      <div className="animate-fade-in pb-24 lg:pb-10">
        {/* Premium Header */}
        <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-xl border-b border-gray-100/50 shadow-[0_4px_20px_rgba(0,0,0,0.03)] transition-all">
          <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-center">
            <h1 className="text-lg font-black tracking-tight text-center">{t('nav.orders')}</h1>
          </div>
        </header>

        {/* Search Bar */}
        {/* {orders.length > 0 && (
          <div className="px-4 mt-4 mb-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
              <Input
                type="search"
                placeholder={currentLang === 'bn' ? 'অর্ডার বা প্রোডাক্ট খুঁজুন...' : 'Search orders or products...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 !border-none !outline-none !ring-0 focus-visible:!ring-0 focus-visible:!border-none shadow-[1px_2px_4px_rgba(0,0,0,.1)] bg-white text-black placeholder:text-gray-600 rounded-xl"
              />
            </div>
          </div>
        )} */}

        {/* Active / In Review / History Tabs */}
        {orders.length > 0 && (
          <div className="px-4 mt-4 max-w-md mx-auto">
            <div className="flex p-1.5 bg-gray-100/80 backdrop-blur-md rounded-2xl">
              <button
                onClick={() => setActiveTab('active')}
                className={`flex-1 relative py-2.5 px-3 rounded-xl text-xs sm:text-sm font-bold transition-all duration-300 ${
                  activeTab === 'active'
                    ? 'bg-white text-black shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                  <span className="flex items-center justify-center gap-1.5">
                    {currentLang === 'bn' ? 'সক্রিয়' : 'Active'}
                    {activeOrders.length > 0 && (
                      <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-black ${
                        activeTab === 'active' ? 'bg-black text-white' : 'bg-gray-200 text-gray-500'
                      }`}>
                        {activeOrders.length}
                      </span>
                    )}
                  </span>
              </button>
              <button
                onClick={() => setActiveTab('in-review')}
                className={`flex-1 relative py-2.5 px-3.5 rounded-xl text-xs sm:text-sm font-bold transition-all duration-300 ${
                  activeTab === 'in-review'
                    ? 'bg-white text-black shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                  <span className="flex items-center justify-center gap-1.5">
                    {currentLang === 'bn' ? 'রিভিউ' : 'To Review'}
                    {inReviewOrders.length > 0 && (
                      <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-black ${
                        activeTab === 'in-review' ? 'bg-black text-white' : 'bg-gray-200 text-gray-500'
                      }`}>
                        {inReviewOrders.length}
                      </span>
                    )}
                  </span>
              </button>
              <button
                onClick={() => setActiveTab('delivered')}
                className={`flex-1 relative py-2.5 px-0 rounded-xl text-xs sm:text-sm font-bold transition-all duration-300 ${
                  activeTab === 'delivered'
                    ? 'bg-white text-black shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                  <span className="flex items-center justify-center gap-1.5">
                    {currentLang === 'bn' ? 'বিগত অর্ডারসমূহ' : 'History'}
                  </span>
              </button>
            </div>
          </div>
        )}

        <div className="px-4 mt-5 space-y-4">
          <AnimatePresence mode="popLayout">
            {isLoading && orders.length === 0 ? (
              <motion.div
                key="loading-skeletons"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-4"
              >
                {[1, 2, 3, 4].map((i) => (
                  <OrderCardSkeleton key={`skeleton-${i}`} />
                ))}
              </motion.div>
            ) : currentOrders.length > 0 ? (
              <motion.div
                key={`${activeTab}-order-list`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="grid grid-cols-1 sm:grid-cols-2 gap-4"
              >
                {currentOrders.map((order, index) => {
                  const itemCount = getItemCount(order);
                  const productImages = getProductImages(order);
                  const isDelivered = order.status === 'delivered';
                  const isCancelled = order.status === 'cancelled';
                  
                  const firstItem = order.order_items?.[0];
                  const firstProductName = currentLang === 'bn' ? firstItem?.products?.name_bn : firstItem?.products?.name_en;

                  return (
                    <motion.div
                      key={order.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.2, delay: index * 0.05 }}
                    >
                      <Card className="group relative overflow-hidden bg-white border border-black rounded-xl shadow-[0_6px_20px_rgba(0,0,0,0.08)] hover:shadow-[0_6px_20px_rgba(0,0,0,0.08)] hover:border-gray-300 transition-all duration-300">
                        <CardContent className="p-0 -my-3 -mb-5 cursor-pointer" onClick={() => navigate(`/orders/${order.id}`)}>
                          {/* Top: Image + Info */}
                          <div className="flex gap-3 px-2.5">
                            {/* Product Image */}
                            <div className="relative w-24 h-24 sm:w-28 sm:h-28 shrink-0 rounded-xl bg-gray-50 border border-gray-100 overflow-hidden">
                              {productImages.length > 0 ? (
                                <img
                                  src={productImages[0]}
                                  alt={firstProductName || 'Product'}
                                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Package className="h-8 w-8 text-gray-300" />
                                </div>
                              )}
                              {itemCount > 1 && (
                                <div className="absolute -top-0.5 -right-0.5 min-w-[20px] h-5 px-1 flex items-center justify-center rounded-full bg-gray-900 text-white text-[10px] font-black ring-2 ring-white">
                                  +{itemCount - 1}
                                </div>
                              )}
                            </div>

                            {/* Info */}
                            <div className="flex-1 min-w-0 flex flex-col justify-center gap-1.5">
                              <h3 className="text-sm font-black text-black leading-snug truncate">
                                {firstProductName || (currentLang === 'bn' ? 'অর্ডার' : 'Order')}
                                {itemCount > 1 && (
                                  <span className="text-black font-semibold">
                                    {' '}+{itemCount - 1} {currentLang === 'bn' ? 'ও অন্যান্য' : 'more'}
                                  </span>
                                )}
                              </h3>

                              <div className={`self-start inline-flex items-center gap-1 pt-0.5 text-[12px] font-black uppercase tracking-wide text-${getStatusColor(order.status)}`}>
                                {getStatusIcon(order.status)}
                                <span>{t(`order.status.${order.status}`)}</span>
                              </div>

                              <div className="flex items-center gap-1.5">
                                <span className="text-lg font-black text-black">Tk {order.total.toFixed(0)}</span>
                                <span className="w-1 h-1 bg-black rounded-full shrink-0" />
                                <span className="text-[12px] font-bold text-black truncate">
                                  {new Date(order.created_at).toLocaleDateString(currentLang === 'bn' ? 'bn-BD' : 'en-US', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="flex gap-2 px-3 py-2.5">
                            <Button
                              variant="outline"
                              className="flex-1 h-9 text-[13px] rounded-xl border-none text-primary font-bold hover:bg-white hover:text-primary px-2"
                              onClick={(e) => { e.stopPropagation(); navigate(`/orders/${order.id}`); }}
                            >
                              {currentLang === 'bn' ? 'বিস্তারিত' : 'View Details'}
                            </Button>

                            {activeTab === 'in-review' || (isDelivered && activeTab !== 'delivered') ? (
                              <Button
                                variant="outline"
                                className="flex-1 h-9 text-[13px] gap-1.5 rounded-xl chocolate-beveled text-white font-semibold border-none hover:bg-chocolate-beveled hover:text-white transition-all px-2"
                                onClick={(e) => { e.stopPropagation(); handleRateProduct(order.id); }}
                              >
                                <span className="truncate">{currentLang === 'bn' ? 'রেট করুন' : 'Rate Product'}</span>
                              </Button>
                            ) : isCancelled ? (
                              <Button
                                variant="outline"
                                className="flex-1 h-9 text-[13px] gap-1.5 rounded-xl bg-gray-50 border-gray-200 text-gray-400 px-2"
                                disabled
                              >
                                <span className="truncate">{currentLang === 'bn' ? 'বাতিল' : 'Cancelled'}</span>
                              </Button>
                            ) : (order.payment_status === 'pending' && order.payment_method !== 'cod') ? (
                              <Button
                                variant="outline"
                                disabled={initiatingPaymentId === order.id}
                                className="flex-1 h-9 text-[13px] gap-1.5 rounded-xl bg-green-700 hover:bg-green-700 hover:text-white text-white font-semibold border-none transition-all px-2 beveled-shadow"
                                onClick={(e) => handlePayNow(e, order)}
                              >
                                {initiatingPaymentId === order.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <span className="truncate">{currentLang === 'bn' ? `এখনই পেমেন্ট করুন (${formatPaymentMethod(order.payment_method || '')})` : `Pay Now (${formatPaymentMethod(order.payment_method || '')})`}</span>
                                )}
                              </Button>
                            ) : (
                              <Button
                                variant="outline"
                                className="flex-1 h-9 text-[13px] gap-1.5 rounded-xl chocolate-beveled text-white font-semibold border-none hover:bg-chocolate-beveled hover:text-white transition-all px-2"
                                onClick={(e) => { e.stopPropagation(); navigate(`/orders/${order.id}/track`); }}
                              >
                                <span className="truncate">{currentLang === 'bn' ? 'ট্র্যাক করুন' : 'Track Order'}</span>
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </motion.div>
            ) : orders.length > 0 ? (
              <motion.div
                key={`${activeTab}-no-matches`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center py-20 text-center"
              >
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Search className="h-10 w-10 text-gray-600" />
                </div>
                <h3 className="text-2xl font-semibold text-black">
                  {searchQuery
                    ? (currentLang === 'bn' ? 'কিছু পাওয়া যায়নি' : 'No results found')
                    : activeTab === 'in-review'
                      ? (currentLang === 'bn' ? 'রিভিউ করার মতো অর্ডার নেই' : 'No orders to review')
                      : activeTab === 'delivered'
                        ? (currentLang === 'bn' ? 'কোনো রিভিউ করা অর্ডার নেই' : 'No reviewed orders yet')
                        : (currentLang === 'bn' ? 'কোনো সক্রিয় অর্ডার নেই' : 'No active orders')}
                </h3>
                <p className="text-sm text-black mt-1 max-w-[240px]">
                  {searchQuery
                    ? (currentLang === 'bn'
                      ? 'আপনার অনুসন্ধানের বিপরীতে কোনো অর্ডার পাওয়া যায়নি'
                      : "We couldn't find any orders matching your search")
                    : activeTab === 'in-review'
                      ? (currentLang === 'bn'
                        ? 'ডেলিভারি সম্পন্ন হলে এখানে রেট করার অপশন দেখা যাবে'
                        : 'Delivered orders waiting for a rating will appear here')
                      : activeTab === 'delivered'
                        ? (currentLang === 'bn'
                          ? 'রেট করা ডেলিভার্ড অর্ডার এখানে দেখা যাবে'
                          : 'Rated delivered orders will appear here')
                        : (currentLang === 'bn'
                          ? 'চলমান অর্ডার এখানে দেখা যাবে'
                          : 'Orders in progress will appear here')}
                </p>
                {searchQuery && (
                  <Button
                    variant="outline"
                    onClick={() => setSearchQuery('')}
                    className="mt-6 rounded-full px-8"
                  >
                    {currentLang === 'bn' ? 'সার্চ মুছুন' : 'Clear search'}
                  </Button>
                )}
              </motion.div>
            ) : (
              <motion.div
                key="no-orders"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex flex-col items-center py-20 text-center"
              >
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <IoReceiptOutline className="h-10 w-10 text-gray-600" />
                </div>
                <h3 className="text-2xl font-semibold text-black">
                  {currentLang === 'bn' ? 'কোনো অর্ডার নেই' : 'No orders found'}
                </h3>
                <p className="text-sm text-black mt-1 max-w-[240px]">
                  {currentLang === 'bn'
                    ? 'আপনার এখনো কোনো অর্ডার নেই'
                    : "You have no orders yet"}
                </p>
                <Link to="/products" className="mt-4">
                  <Button size="lg" className="rounded-full px-6 text-white font-bold">
                    {currentLang === 'bn'
                      ? 'কেনাকাটা শুরু করুন'
                      : "Start Shopping"}
                  </Button>
                </Link>
              </motion.div>
            )}
          </AnimatePresence>

          {totalOrders > 20 && !searchQuery && (
            <div className="flex justify-center items-center gap-4 mt-8 pb-8">
              <Button
                variant="outline"
                disabled={currentPage === 1}
                onClick={() => { setCurrentPage(p => p - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="rounded-xl px-6"
              >
                {currentLang === 'bn' ? 'আগের পাতা' : 'Previous'}
              </Button>
              <span className="text-sm font-bold text-gray-600">
                {currentPage} / {Math.ceil(totalOrders / 20)}
              </span>
              <Button
                variant="outline"
                disabled={currentPage * 20 >= totalOrders}
                onClick={() => { setCurrentPage(p => p + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="rounded-xl px-6"
              >
                {currentLang === 'bn' ? 'পরের পাতা' : 'Next'}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Rating Dialog */}
      <AnimatePresence>
        {ratingOrderId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4"
            onClick={() => setRatingOrderId(null)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-2xl"
            >
              <h3 className="text-lg font-black text-black text-center mb-1">
                {currentLang === 'bn' ? 'প্রোডাক্ট রেট করুন' : 'Rate Your Order'}
              </h3>
              <p className="text-xs text-gray-500 text-center mb-6">
                {currentLang === 'bn' ? 'আপনার অভিজ্ঞতা কেমন ছিল?' : 'How was your experience?'}
              </p>
              <div className="flex justify-center gap-2 mb-6">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    onClick={() => setRatingValue(star)}
                    className="transition-transform hover:scale-110 active:scale-95"
                  >
                    <Star
                      className={`h-8 w-8 ${
                        star <= ratingValue
                          ? 'text-amber-400 fill-amber-400'
                          : 'text-gray-200'
                      } transition-colors`}
                    />
                  </button>
                ))}
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 rounded-xl font-bold h-11"
                  onClick={() => setRatingOrderId(null)}
                >
                  {currentLang === 'bn' ? 'বাদ দিন' : 'Skip'}
                </Button>
                <Button
                  className="flex-1 rounded-xl font-bold h-11 bg-black hover:bg-gray-800 text-white"
                  disabled={ratingValue === 0}
                  onClick={submitRating}
                >
                  {currentLang === 'bn' ? 'জমা দিন' : 'Submit'}
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}